import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CgSpinner } from "react-icons/cg";
import { useDispatch, useSelector } from "react-redux";
import { axios } from "../../../../Global";
import { setAppError } from "../../../../store/userSlice";
import {
  savePreviousState,
  validateChangeAtButton,
} from "../../../../utils/EditChangeValidate";
import { RiArrowDownSLine, RiArrowRightSLine } from "react-icons/ri";
import SearchFilter from "../../../../components/SmartDataTable/SearchFilter";
import Highlighter from "react-highlight-words";

export default function AddEditForm({
  submitForm,
  submitFormLoading,
  cancelForm,
  loadedValues = {},
  form = [],
  component,
  getPermissionList,
}) {
  const dispatch = useDispatch();
  const permissionLists = useSelector(
    (state) => state.roleManagement.permissionLists
  );
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allowEdit, setAllowEdit] = useState(!loadedValues);
  const [showRoles, setShowRoles] = useState(!loadedValues);

  const [prevState, setPrevState] = useState([]);

  useEffect(() => {
    if (loadedValues) {
      setPrevState(savePreviousState(form));
    }
  }, []);

  useEffect(() => {
    if (prevState?.length > 0) {
      const valid = validateChangeAtButton(prevState, form);
      if (valid || selectedPermissions.length !== 0) setAllowEdit((p) => true);
      else setAllowEdit((p) => false);
    }
  }, [form, selectedPermissions]);

  async function getselectedPermissions(id) {
    setLoading(true);
    try {
      if (id) {
        const token = localStorage.getItem("token");
        const response = await axios.get(`/dashboard/roles/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSelectedPermissions(response?.data?.data?.role?.permissions);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);

      dispatch(
        setAppError({
          msg: error.message,
        })
      );
    }
  }

  useEffect(() => {
    if (permissionLists.length <= 0) {
      dispatch(getPermissionList());
    }
    if (loadedValues?.method === "edit") {
      getselectedPermissions(loadedValues.editId);
    }
  }, [dispatch, loadedValues, getPermissionList, permissionLists.length]);

  const [searchTerm, setSearchTerm] = useState("");
  const currentSearchWords = useMemo(() => {
    return searchTerm ? searchTerm.split(/\s+/).filter(Boolean) : [];
  }, [searchTerm]);
  const [expandedModules, setExpandedModules] = useState({});

  const toggleModule = (moduleName) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleName]: !prev[moduleName],
    }));
  };

  const categorizePermissions = (permissions) => {
    const mapping = {
      list: ["list", "view"],
      create: ["create", "add"],
      edit: ["edit", "update"],
      delete: ["delete", "removed", "remove"],
    };
    const grouped = {};

    permissions.forEach((p) => {
      const moduleName = p.module || "Others";
      const name = p.name.toLowerCase();
      
      let action = null;
      let baseName = name;
      
      for (const [key, aliases] of Object.entries(mapping)) {
        for (const alias of aliases) {
          if (name.endsWith(`-${alias}`)) {
            action = key;
            baseName = name.substring(0, name.length - alias.length - 1);
            break;
          }
        }
        if (action) break;
      }

      if (!grouped[moduleName]) {
        grouped[moduleName] = {
          name: moduleName,
          subModules: {},
        };
      }
      
      if (!grouped[moduleName].subModules[baseName]) {
        grouped[moduleName].subModules[baseName] = {
          module: moduleName,
          subModule: baseName,
          list: null,
          create: null,
          edit: null,
          delete: null,
          others: [],
        };
      }

      if (action) {
        grouped[moduleName].subModules[baseName][action] = p;
      } else {
        grouped[moduleName].subModules[baseName].others.push(p);
      }
    });

    return Object.values(grouped)
      .map(mod => ({
        ...mod,
        subModules: Object.values(mod.subModules).sort((a, b) => a.subModule.localeCompare(b.subModule))
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const categorizedPermissions = useMemo(() => {
    return categorizePermissions(permissionLists);
  }, [permissionLists]);

  const filteredPermissions = useMemo(() => {
    if (!searchTerm) return categorizedPermissions;
    const term = searchTerm.toLowerCase();
    return categorizedPermissions.filter(mod => {
      const matchModule = mod.name.toLowerCase().includes(term);
      const matchSub = mod.subModules.some(sub => sub.subModule.toLowerCase().includes(term));
      return matchModule || matchSub;
    }).map(mod => {
      if (mod.name.toLowerCase().includes(term)) return mod;
      return {
        ...mod,
        subModules: mod.subModules.filter(sub => sub.subModule.toLowerCase().includes(term))
      };
    });
  }, [categorizedPermissions, searchTerm]);

  const rolesSelectorHandler = useCallback(
    (permission) => {
      setSelectedPermissions((prev) => {
        if (prev.find((p) => p.id == permission.id)) {
          return prev.filter((p) => p.id != permission.id);
        }
        return [...prev, permission];
      });
    },
    [permissionLists]
  );

  const rolesGroupChangeHandler = useCallback((subGroup, checked) => {
    const groupPermissions = [
      subGroup.list,
      subGroup.create,
      subGroup.edit,
      subGroup.delete,
      ...subGroup.others,
    ].filter(Boolean);

    const ids = groupPermissions.map((p) => p.id);

    setSelectedPermissions((prev) => {
      if (checked) {
        const newOnes = groupPermissions.filter(
          (p) => !prev.find((sp) => sp.id === p.id)
        );
        return [...prev, ...newOnes];
      } else {
        return prev.filter((p) => !ids.includes(p.id));
      }
    });
  }, []);

  const moduleGroupChangeHandler = useCallback((module, checked) => {
    const allPermissions = module.subModules.flatMap(sub => [
      sub.list,
      sub.create,
      sub.edit,
      sub.delete,
      ...sub.others,
    ]).filter(Boolean);

    const ids = allPermissions.map(p => p.id);

    setSelectedPermissions(prev => {
      if (checked) {
        const newOnes = allPermissions.filter(p => !prev.find(sp => sp.id === p.id));
        return [...prev, ...newOnes];
      } else {
        return prev.filter(p => !ids.includes(p.id));
      }
    });
  }, []);

  const getDisplayLabel = (group) => {
    const sub = group?.subModule || "";
    return typeof sub === "string" ? sub.toUpperCase().replace(/-/g, " ") : "";
  };

  return (
    <div className="flex justify-center items-center bg-white dark:bg-dark rounded-md py-5 w-full">
      {loading ? (
        <div className="flex justify-center items-center gap-3 mt-5 w-full col-span-4">
          <p>Loading</p>
          <CgSpinner className="animate-spin" />
        </div>
      ) : (
        <form
          method="POST"
          onSubmit={(event) => submitForm(event, selectedPermissions)}
          className="w-full flex flex-col items-center justify-center px-4"
        >
          <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4">
            {form.map((elem, index) => (
              <div
                key={index}
                className={`flex flex-col p-2 gap-1 w-full ${
                  elem.hidden ? "hidden" : ""
                }`}
              >
                <label htmlFor={`addOrEdit-${elem.name}`} className="text-xs ">
                  {elem.text}
                </label>
                <input
                  type={elem.type}
                  name={elem.name}
                  id={`addOrEdit-${elem.name}`}
                  placeholder={`Enter ${elem.text}`}
                  required
                  className="border-[1px] border-border dark:bg-dark rounded-md p-1 py-2 text-xs"
                  defaultValue={elem.value}
                  onChange={(e) => {
                    const errorElem = document.getElementById(
                      `${component}-${elem.columnName}`
                    );
                    if (errorElem) errorElem.style.display = "none";
                    elem.update(e.target.value);
                  }}
                  disabled={elem.disabled ? true : false}
                />
                <p
                  id={`${component}-${elem.columnName}`}
                  className="hidden w-full h-5 bg-red/5 text-red text-xs"
                ></p>
              </div>
            ))}
          </div>

          <div className="w-full max-w-6xl m-3 mt-8 flex flex-col">
            <div className="flex flex-wrap justify-between items-center gap-4 px-5 py-3 bg-gray-50 dark:bg-dark2 rounded-t-md border-b">
              <div>
                <h2 className="font-semibold uppercase text-sm">Permissions Table</h2>
              </div>
              <div className="flex items-center gap-3">
                <SearchFilter
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  placeholder="Search modules..."
                />
                <button
                  className="p-1 rounded-md ring-1 ring-gray shadow-sm hover:scale-105 bg-white dark:bg-dark"
                  type="button"
                  onClick={() => setShowRoles((p) => !p)}
                >
                  {showRoles ? (
                    <RiArrowDownSLine size={"1.2rem"} />
                  ) : (
                    <RiArrowRightSLine size={"1.2rem"} />
                  )}
                </button>
              </div>
            </div>

            {showRoles && (
              <div className="overflow-x-auto bg-white dark:bg-dark rounded-lg shadow max-h-[70vh] w-full relative scroll-smooth shadow-inner border border-gray-100 dark:border-white/5 rounded-sm">
                  <table className="min-w-full text-left">
                  <thead className="bg-red dark:bg-red text-white uppercase text-base font-semibold tracking-wider sticky top-0 z-30 shadow-md">
                    <tr>
                      <th className="px-6 py-4 border-b border-white/10 text-center w-20">SL No.</th>
                      <th className="px-6 py-4 border-b border-white/10 w-[30%]">Module / Section</th>
                      <th className="px-6 py-4 border-b border-white/10 text-center w-24">All</th>
                      <th className="px-6 py-4 border-b border-white/10 text-center w-32">List / View</th>
                      <th className="px-6 py-4 border-b border-white/10 text-center w-32">Create / Add</th>
                      <th className="px-6 py-4 border-b border-white/10 text-center w-32">Edit / Update</th>
                      <th className="px-6 py-4 border-b border-white/10 text-center w-32">Delete / Remove</th>
                      <th className="px-6 py-4 border-b border-white/10">Others</th>
                    </tr>
                  </thead>
                  {filteredPermissions.map((module, modIndex) => {
                    const allSubPermissions = module.subModules.flatMap(sub => [
                      sub.list, sub.create, sub.edit, sub.delete, ...sub.others
                    ]).filter(Boolean);
                    const isModuleSelected = allSubPermissions.length > 0 && allSubPermissions.every(p => 
                      selectedPermissions.some(sp => sp.id === p.id)
                    );
                    const isExpanded = expandedModules[module.name] || searchTerm;

                    return (
                      <tbody key={module.name} className="border-b bg-white dark:bg-dark">
                        <tr className="bg-gray-100 dark:bg-slate-800 font-bold shadow-sm text-gray-800 dark:text-white">
                          <td className="px-6 py-4 border-b border-gray-200 dark:border-white/5 text-center bg-gray-100 dark:bg-slate-800 w-20 text-base">{modIndex + 1}</td>
                          <td className="px-6 py-4 border-b border-gray-200 dark:border-white/5 flex items-center gap-2 cursor-pointer bg-gray-100 dark:bg-slate-800 w-full" onClick={() => toggleModule(module.name)}>
                            <span className="transition-transform duration-200 text-gray-600 dark:text-gray-300" style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                              <RiArrowDownSLine size="1.2rem" />
                            </span>
                            <span className="uppercase text-gray-800 dark:text-white truncate text-base">
                              <Highlighter
                                searchWords={currentSearchWords}
                                autoEscape={true}
                                textToHighlight={module.name}
                              />
                            </span>
                          </td>
                          <td className="px-6 py-4 border-b border-gray-200 dark:border-white/5 text-center bg-gray-100 dark:bg-slate-800 w-24">
                            <div className="flex items-center justify-center h-full">
                              <input
                                type="checkbox"
                                className="toggle toggle-error toggle-sm border-gray-300 dark:border-white"
                                checked={isModuleSelected}
                                onChange={(e) => moduleGroupChangeHandler(module, e.target.checked)}
                              />
                            </div>
                          </td>
                          <td colSpan={5} className="px-6 py-4 border-b border-gray-200 dark:border-white/5 bg-gray-100 dark:bg-slate-800 italic text-sm text-gray-500 dark:text-gray-400 align-middle">
                            {module.subModules.length} Sub-sections
                          </td>
                        </tr>
                        {isExpanded && module.subModules.map((sub, subIndex) => {
                          const subPermissions = [
                            sub.list, sub.create, sub.edit, sub.delete, ...sub.others
                          ].filter(Boolean);
                          const isSubSelected = subPermissions.every(p => 
                            selectedPermissions.some(sp => sp.id === p.id)
                          );

                          return (
                            <tr
                              key={`${module.name}-${sub.subModule}`}
                              className={`${
                                subIndex % 2 === 0 ? "bg-white dark:bg-dark" : "bg-gray-50 dark:bg-dark2"
                              } hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors border-b border-gray-100 dark:border-white/5`}
                            >
                              <td className="px-6 py-6 border-b text-center text-gray-400 text-sm w-20">{modIndex + 1}.{subIndex + 1}</td>
                              <td className="px-6 py-6 pl-12 border-b font-medium text-gray-700 dark:text-gray-300 w-[30%] truncate text-base" title={getDisplayLabel(sub)}>
                                <Highlighter
                                  searchWords={currentSearchWords}
                                  autoEscape={true}
                                  textToHighlight={getDisplayLabel(sub)}
                                />
                              </td>
                              <td className="px-6 py-6 border-b text-center w-24">
                                <div className="flex items-center justify-center h-full">
                                  <input
                                    type="checkbox"
                                    className="toggle toggle-error toggle-xs"
                                    checked={isSubSelected}
                                    onChange={(e) =>
                                      rolesGroupChangeHandler(sub, e.target.checked)
                                    }
                                  />
                                </div>
                              </td>
                              <td className="px-6 py-6 border-b text-center w-32">
                                {sub.list && (
                                  <div className="flex flex-col items-center gap-1">
                                    <input
                                      type="checkbox"
                                      className="toggle toggle-error toggle-xs"
                                      checked={selectedPermissions.some(
                                        (p) => p.id === sub.list.id
                                      )}
                                      onChange={() => rolesSelectorHandler(sub.list)}
                                    />
                                    <span className="text-[10px] opacity-70 uppercase font-semibold text-red">
                                      {sub.list.name.split("-").pop()}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-6 border-b text-center w-32">
                                {sub.create && (
                                  <div className="flex flex-col items-center gap-1">
                                    <input
                                      type="checkbox"
                                      className="toggle toggle-error toggle-xs"
                                      checked={selectedPermissions.some(
                                        (p) => p.id === sub.create.id
                                      )}
                                      onChange={() => rolesSelectorHandler(sub.create)}
                                    />
                                    <span className="text-[10px] opacity-70 uppercase font-semibold text-red">
                                      {sub.create.name.split("-").pop()}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-6 border-b text-center w-32">
                                {sub.edit && (
                                  <div className="flex flex-col items-center gap-1">
                                    <input
                                      type="checkbox"
                                      className="toggle toggle-error toggle-xs"
                                      checked={selectedPermissions.some(
                                        (p) => p.id === sub.edit.id
                                      )}
                                      onChange={() => rolesSelectorHandler(sub.edit)}
                                    />
                                    <span className="text-[10px] opacity-70 uppercase font-semibold text-red">
                                      {sub.edit.name.split("-").pop()}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-6 border-b text-center w-32">
                                {sub.delete && (
                                  <div className="flex flex-col items-center gap-1">
                                    <input
                                      type="checkbox"
                                      className="toggle toggle-error toggle-xs"
                                      checked={selectedPermissions.some(
                                        (p) => p.id === sub.delete.id
                                      )}
                                      onChange={() => rolesSelectorHandler(sub.delete)}
                                    />
                                    <span className="text-[10px] opacity-70 uppercase font-semibold text-red">
                                      {sub.delete.name.split("-").pop()}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-6 border-b">
                                <div className="flex flex-wrap gap-2">
                                  {sub.others.map((p) => (
                                    <label
                                      key={p.id}
                                      className="flex items-center gap-1 bg-red/5 dark:bg-red/10 p-2 rounded cursor-pointer hover:bg-red/10 dark:hover:bg-red/20 transition-colors border border-transparent hover:border-red/20"
                                    >
                                      <input
                                        type="checkbox"
                                        className="toggle toggle-error toggle-xs"
                                        checked={selectedPermissions.some(
                                          (sp) => sp.id === p.id
                                        )}
                                        onChange={() => rolesSelectorHandler(p)}
                                      />
                                      <span className="text-[11px] whitespace-nowrap font-semibold text-red uppercase">
                                        {p.name.split("-").pop()}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    );
                  })}
                </table>
              </div>
            )}

            <p
              id={`${component}-roles`}
              className="hidden w-full h-5 bg-red/5 text-red text-xs  max-w-md p-2"
            ></p>
          </div>

          <div className="p-2 w-full max-w-md flex gap-3 mt-5">
            <button
              className="ring-1 ring-black dark:ring-red w-full text-xs dark:text-white rounded-md py-2 flex justify-center items-center gap-2"
              type="button"
              disabled={submitFormLoading}
              onClick={cancelForm}
            >
              <p>Cancel</p>
            </button>
            {allowEdit ? (
              <button
                className="bg-black dark:bg-red w-full text-xs text-white rounded-md py-2 flex justify-center items-center gap-2"
                type="submit"
                disabled={submitFormLoading}
              >
                <p>Submit</p>
                {submitFormLoading && <CgSpinner className="animate-spin" />}
              </button>
            ) : (
              <button
                disabled
                className="bg-gray cursor-not-allowed dark:bg-red w-full text-xs text-white rounded-md py-2 flex justify-center items-center gap-2"
                title="no changes made"
              >
                <p>Submit</p>
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
